// z10–z12 (Diego, 14-oct-2026; 16-oct: «el que hiciste está muy difícil y confuso… que en un texto le puedas escribir
// todos los equipos y describir todo lo que va a tener, y que con IA salga el estimado»). Cliente NUEVO (sin recibos) de
// CASA o NEGOCIO: el vendedor describe en texto libre la casa o el negocio y sus equipos; la IA de z25 («corregir», el
// mismo modelo de Configuración → IA) propone el cuadro de cargas con un supuesto por equipo, y de ahí sale el consumo
// MES POR MES con el calor de Sonora, igual que lo calcula el motor (los aires solo en los meses calientes). El vendedor
// corrige la tabla antes de cotizar. Aquí vive lo que comparten la IA (real y simulada), el servidor y la pantalla.
import { numero, pesos } from './dinero.ts';
import { PERFIL_CONSUMO_CASA } from './ajuste-consumos.ts';
import type { Carga, TipoCliente } from './dominio/cuestionario.ts';

/** Copia de paquetes/motor/datos/consumo-perfiles.json (dias_mes_cuadro_cargas y meses_mas_calidos): el motor TRUENA al
 *  cargar si dejan de coincidir (datos.ts), para que la pantalla no prometa un mes por mes distinto del que cotiza. */
export const DIAS_MES_CUADRO_CARGAS = 30.4;
export const MESES_MAS_CALIDOS: readonly number[] = Object.freeze([8, 7, 6, 9, 5, 10, 4, 3, 11, 2, 12, 1]);

/** Así empieza la instrucción que el servidor le manda a «corregir» para estimar (la IA simulada la reconoce por esto). */
export const MARCA_ESTIMAR = 'ESTIMAR CONSUMO DE CLIENTE NUEVO';
export const RUTA_CARGAS_ESTIMACION = '/cuestionario/estimacion/cargas/-';
/** Lo que el vendedor puede escribir (cabe con la instrucción en el tope de «corregir»). */
export const MAX_DESCRIPCION = 500;
/** Prefijo de `estimacion.fuente` cuando la estimación sale de la descripción (la pantalla recupera el texto de ahí). */
export const PREFIJO_FUENTE_DESCRIPCION = 'Descripción del vendedor (IA): ';

/** ADENDA z46-2 (P22): la frase que marca el consumo estimado con lo que paga (pantalla, motor y PDF). */
export function fuenteDePago(p: { monto: number; periodo: 'bimestral' | 'mensual' }): string {
  return `Estimado con lo que pagas a CFE: ${pesos(p.monto)} ${p.periodo === 'bimestral' ? 'al bimestre' : 'al mes'} con IVA`;
}

/** kWh al mes de un renglón del cuadro de cargas en los meses que se usa (la misma cuenta que el motor). */
export function kwhMesDeCarga(c: Pick<Carga, 'cantidad' | 'w' | 'horas_dia'>): number {
  return (c.cantidad * c.w * c.horas_dia * DIAS_MES_CUADRO_CARGAS) / 1000;
}

/** Consumo por mes calendario (ENE…DIC) del cuadro de cargas, como lo reparte el motor: lo de 12 meses va parejo todo el año.
 *  Lo de menos meses (los aires):
 *  · `estacional` (ADENDA z48-10, Diego 28-oct: «la IA pone 6 meses todos iguales y otros 6 iguales pero más bajos, cuando
 *    debería estimar como una curva como los consumos reales»; parámetro de versión `consumo_estacional`, solo cotizaciones
 *    NUEVAS): su energía del año (kWh al mes × meses de uso) se reparte mes a mes con la curva MEDIDA de casas de Sonora
 *    (PERFIL_CONSUMO_CASA: sube en primavera, pico en julio–agosto, baja en otoño, mínimo en invierno). El total del año no
 *    cambia. En un negocio la curva sale más plana sola: su carga pareja (refrigeración, luces) pesa más.
 *  · sin `estacional` (las guardadas antes): en los N meses más calientes, parejo (dos bloques). */
export function consumoPorMesDeCargas(cargas: readonly Pick<Carga, 'cantidad' | 'w' | 'horas_dia' | 'meses_uso'>[], opciones: { estacional?: boolean } = {}): number[] {
  const porMes = Array<number>(12).fill(0);
  const suma = PERFIL_CONSUMO_CASA.reduce((a, b) => a + b, 0);
  for (const g of cargas) {
    const k = kwhMesDeCarga(g);
    if (g.meses_uso >= 12) {
      for (let m = 0; m < 12; m++) porMes[m]! += k;
      continue;
    }
    const n = Math.max(0, Math.round(g.meses_uso));
    if (opciones.estacional) {
      for (let m = 0; m < 12; m++) porMes[m]! += (k * n * PERFIL_CONSUMO_CASA[m]!) / suma;
      continue;
    }
    for (const m of MESES_MAS_CALIDOS.slice(0, n)) porMes[m - 1]! += k;
  }
  return porMes;
}

/** La instrucción para la IA de «corregir» (z25): el cuadro de cargas como ediciones «agregar», un supuesto por equipo. */
export function instruccionEstimar(descripcion: string, tipo: TipoCliente): string {
  const quien = tipo === 'casa' ? 'una casa' : 'un negocio';
  return [
    `${MARCA_ESTIMAR} (${quien} en Hermosillo, Sonora, sin recibos): propone su cuadro de cargas, un «agregar» por equipo en`,
    `${RUTA_CARGAS_ESTIMACION}, valor_json {"equipo","cantidad","w","horas_dia","meses_uso"} (w de uno; meses_uso = meses más`,
    'calientes en que se usa: aires 4 a 7, lo de todo el año 12); equipo = nombre corto, hasta 20 letras («Minisplit 1.5 ton»,',
    '«Focos LED»). Motivo = el supuesto. Suma lo básico (refri, luces, varios).',
    `Descripción: «${descripcion.trim().slice(0, MAX_DESCRIPCION)}»`,
  ].join(' ');
}

/** ADENDA z52-2 (Diego, 28-oct-2026): «si te ponen que va a aumentar el consumo… tú hagas una sugerencia con IA de cuánto
 *  aumentaría con los equipos adicionales y la descripción». Así empieza la instrucción del AUMENTO (la IA simulada la reconoce). */
export const MARCA_AUMENTO = 'ESTIMAR AUMENTO DE CONSUMO';

/** La instrucción para la IA de «corregir» (z25, el mismo modelo, prompt y esfuerzo): SOLO los equipos NUEVOS de la descripción
 *  (lo que ya tiene ya está en su recibo), como ediciones «agregar» del cuadro de cargas. */
export function instruccionAumento(descripcion: string, tipo: TipoCliente, giro: string | null = null): string {
  const g = giro?.trim().replace(/[.!?;:]+$/, '').slice(0, 40);
  const quien = tipo === 'casa' ? 'una casa' : g ? `un negocio (${g})` : 'un negocio';
  return [
    `${MARCA_AUMENTO} (${quien} en Hermosillo, Sonora, que YA tiene recibo de CFE): propone SOLO los equipos NUEVOS de la`,
    `descripción, un «agregar» por equipo en ${RUTA_CARGAS_ESTIMACION}, valor_json {"equipo","cantidad","w","horas_dia","meses_uso"}`,
    '(w de uno). NO agregues lo que ya tiene (luces, refrigeradores, varios): ya está en su recibo. Horas al día según el giro y su',
    `horario (sin dato: ${tipo === 'casa' ? '6 h' : '8 h'}). Aires: meses_uso 7 y otro renglón «… en verano» con las horas de más de junio a`,
    'septiembre (meses_uso 4). equipo = nombre corto, hasta 20 letras. Motivo = el supuesto.',
    `Descripción: «${descripcion.trim().slice(0, MAX_DESCRIPCION)}»`,
  ].join(' ');
}

/** Cómo lo dice la propuesta (hasta MOTIVO_AUMENTO_MAX letras): los equipos nuevos sin el renglón «en verano», unidos con « y »;
 *  si no caben, «equipos nuevos». En minúscula inicial (va a media frase: «ya sumamos aires de 5 ton»). */
export function motivoDeAumento(cargas: readonly Pick<Carga, 'equipo'>[], max = 40): string {
  const nombres = [...new Set(cargas.map((c) => c.equipo.trim()).filter((x) => x && !/\ben verano\b/i.test(x)))];
  const minuscula = (x: string) => (/^[A-ZÁÉÍÓÚÑ][a-záéíóúñ]/.test(x) ? x[0]!.toLowerCase() + x.slice(1) : x);
  const t = nombres.map(minuscula).join(' y ');
  return t.length >= 3 && t.length <= max ? t : 'equipos nuevos';
}

/** ADENDA z52-2, verificación del 28-oct (curva del aumento): los kWh DE MÁS por mes (ENE…DIC) de los equipos NUEVOS. Cada equipo
 *  cuenta en SUS meses de uso, los más calientes primero (MESES_MAS_CALIDOS: 7 meses = abril a octubre; 4 = junio a septiembre),
 *  como dice el supuesto («los aires trabajan de abril a octubre y más fuerte de junio a septiembre»). Antes se repartía con la
 *  curva de las casas (z48-10, la de un cliente NUEVO entero) y 16 % de unos aires nuevos caía de noviembre a marzo (enero: 1.5 h
 *  al día a 5 kW). La MISMA cuenta en el servidor (lo que propone la IA), la pantalla (la tabla de 12 meses) y las reglas. */
export function aumentoPorMesDeCargas(cargas: readonly Pick<Carga, 'cantidad' | 'w' | 'horas_dia' | 'meses_uso'>[]): number[] {
  return consumoPorMesDeCargas(cargas);
}

/** Los kWh al año que dan las REGLAS (sin IA) con esta descripción, solo equipos nuevos (0 si no reconocen ninguno): contra esto se
 *  mide lo que propone la IA (motor → aumentoContraReglas). */
export function kwhAumentoDeReglas(descripcion: string, tipo: TipoCliente): number {
  const est = estimarPorReglas(descripcion, tipo, { soloNuevo: true });
  return aumentoPorMesDeCargas(est.cargas.map((c) => c.carga)).reduce((a, b) => a + b, 0);
}

/** Verificación del 28-oct (candado de la IA): el aumento (kWh al año) contra lo que dan las REGLAS con la misma descripción. null =
 *  no hay contra qué medir (de las reglas, o sin equipos que las reglas reconozcan). `nivel`: rojo con el doble o más (o 40 % o
 *  menos), amarillo arriba de 1.5 veces (o abajo de 60 %). La MISMA cuenta en el motor (su aviso) y en la pantalla (al aceptar). */
export function aumentoContraReglasBase(au: { kwh_mes: readonly number[]; descripcion: string; fuente: string }, tipo: TipoCliente): { total: number; reglas: number; razon: number; nivel: 'rojo' | 'amarillo' | null } | null {
  if (au.fuente === 'reglas') return null;
  const reglas = kwhAumentoDeReglas(au.descripcion, tipo);
  const total = au.kwh_mes.reduce((a, b) => a + b, 0);
  if (!(reglas > 0) || !(total > 0)) return null;
  const razon = total / reglas;
  return { total, reglas, razon, nivel: razon >= 2 || razon <= 0.4 ? 'rojo' : razon > 1.5 || razon < 0.6 ? 'amarillo' : null };
}

export interface CargaEstimada {
  carga: Carga;
  /** El supuesto de ese renglón («1 tonelada ≈ 1,000 W…»). */
  motivo: string;
}

const NUMEROS: Readonly<Record<string, number>> = {
  un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9, diez: 10, once: 11, doce: 12, quince: 15, veinte: 20,
};
const NUM = `(\\d+(?:[.,]\\d+)?|${Object.keys(NUMEROS).join('|')})`;
const valor = (s: string) => NUMEROS[s] ?? Number(s.replace(',', '.'));
/** El número escrito justo antes de la palabra («10 toneladas», «cuatro recámaras»); null si no hay. */
function cuantos(t: string, palabra: string): number | null {
  const m = new RegExp(`${NUM}\\s*(?:de\\s+)?(?:${palabra})`).exec(t);
  return m ? valor(m[1]!) : null;
}
const menciona = (t: string, palabra: string) => new RegExp(`\\b(?:${palabra})`).test(t);
/** «3 minisplits de 2 toneladas» + «un clima de 1.5 ton»: la suma de cantidad × tamaño de cada grupo (sin cantidad = 1);
 *  null si ningún equipo dice su tamaño. `tamano` es la unidad («toneladas?|ton\\b», «hp|caballos?»). */
function sumaPorEquipo(t: string, equipo: string, tamano: string): { equipos: number; total: number } | null {
  const re = new RegExp(`(?:${NUM}\\s*)?(?:${equipo})\\s+(?:de\\s+)?${NUM}\\s*(?:${tamano})`, 'g');
  let equipos = 0, total = 0;
  for (const m of t.matchAll(re)) {
    const n = m[1] ? valor(m[1]) : 1;
    equipos += n;
    total += n * valor(m[2]!);
  }
  return equipos > 0 && total > 0 ? { equipos, total } : null;
}
/** Cuántos dice de cada palabra, sumados («3 refrigeradores … 2 vitrinas» = 5); mencionado sin número = 1; null si nada. */
function cuantosDe(t: string, palabras: readonly string[]): number | null {
  let n = 0, hay = false;
  for (const p of palabras) {
    const c = cuantos(t, p) ?? (menciona(t, p) ? 1 : null);
    if (c !== null) { n += c; hay = true; }
  }
  return hay ? n : null;
}
const AIRES = 'minisplits?|mini splits?|climas?|aires? acondicionados?|aires?';
/** Cámaras de refrigeración: son refrigeración del producto, no aire acondicionado del local. */
const CAMARA_FRIA = 'camaras? (?:de )?(?:refrigeracion|congelacion|frias?)|cuartos? frios?';

/** Estimación SIN IA (la IA simulada, la demo y las pruebas): reglas sencillas y a la vista. Lo que la descripción no
 *  dice no se inventa, salvo lo básico de toda casa o negocio (refrigerador, luces, varios), y eso se dice. */
export function estimarPorReglas(descripcion: string, tipo: TipoCliente, opciones: { soloNuevo?: boolean } = {}): { cargas: CargaEstimada[]; supuestos: string[] } {
  // ADENDA z52-2: con `soloNuevo` (aumento de consumo de quien YA tiene recibo) solo van los equipos que dice la descripción: lo
  // básico (luces, refrigerador, varios) ya está en su recibo.
  const soloNuevo = opciones.soloNuevo === true;
  const t = descripcion.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    // «12000 btu» = 1 tonelada de refrigeración (verificador 23-oct: «3 minisplits de 12000 btu» se contaban a 1.5 ton cada uno)
    .replace(/(\d[\d,]*(?:\.\d+)?)\s*btu\b/g, (_, n: string) => `${Math.round((Number(n.replace(/,/g, '')) / 12000) * 100) / 100} ton`)
    // «compresor de aire de 10 hp» es un compresor (no un aire acondicionado) y sus HP van con él
    .replace(/(compresore?s?)\s+de\s+aire\b/g, '$1');
  const casa = tipo === 'casa';
  const cargas: CargaEstimada[] = [];
  const supuestos: string[] = ['Hermosillo, Sonora: los aires trabajan de abril a octubre y más fuerte de junio a septiembre.'];
  const poner = (equipo: string, cantidad: number, w: number, horas_dia: number, meses_uso: number, motivo: string) =>
    cargas.push({ carga: { equipo: equipo.slice(0, 60), cantidad: Math.min(500, Math.max(1, Math.round(cantidad))), w: Math.round(w), horas_dia, meses_uso }, motivo });

  // Aires: cada equipo por su tamaño («3 minisplits de 2 toneladas» = 6 ton; revisión de ventas 22-oct: antes contaba 2), o
  // toneladas en total («10 toneladas de aire»), o minisplits contados a 1.5 ton cada uno.
  const tAire = t.replace(new RegExp(CAMARA_FRIA, 'g'), ' ');
  const porEquipo = sumaPorEquipo(tAire, AIRES, 'toneladas?|ton\\b|tr\\b');
  let ton = porEquipo?.total ?? cuantos(tAire, 'toneladas?|ton\\b|tr\\b');
  const minis = cuantos(tAire, `${AIRES}\\b`);
  if (ton === null && minis !== null) ton = minis * 1.5;
  if (ton === null && menciona(tAire, 'aire|minisplit|clima|refrigeracion|enfriador')) ton = casa ? 3 : 5;
  if (ton !== null && ton > 0) {
    const w = Math.round(ton * 1000);
    const cuenta = porEquipo && porEquipo.equipos > 1 ? `${numero(porEquipo.equipos)} equipos, ${numero(ton, Number.isInteger(ton) ? 0 : 1)} ton en total; ` : '';
    // nombres cortos, como en un cuadro de cargas: la propuesta los imprime en la caja «Cómo lo estimamos» de la hoja 3
    poner(`Aires de ${numero(ton, Number.isInteger(ton) ? 0 : 1)} ton`, 1, w, casa ? 6 : 8, 7, `${cuenta}1 tonelada ≈ 1,000 W (inverter); ${casa ? 6 : 8} h al día de abril a octubre.`);
    poner('Aires en verano', 1, w, casa ? 5 : 3, 4, `En pleno verano (jun–sep) trabajan ${casa ? 5 : 3} h más al día.`);
  } else if (!soloNuevo) supuestos.push(`No dijo aires: no se incluyeron (en Hermosillo casi ${casa ? 'toda casa' : 'todo negocio'} tiene; agrégalos si los hay).`);

  const recamaras = cuantos(t, 'recamaras?|cuartos?|habitaciones?');
  const pisos = cuantos(t, 'pisos?|plantas?|niveles?');
  if (soloNuevo) {
    // (solo los equipos que dice la descripción, abajo)
    const refris = cuantosDe(t, ['(?:refrigeradore?s?|refris?)\\b', 'vitrinas?']);
    if (refris) poner(casa ? 'Refrigerador' : 'Refrigeradores / vitrinas', refris, casa ? 60 : 250, 24, 12, casa ? 'Promedio con el ciclo del compresor ≈ 60 W las 24 h (≈ 44 kWh al mes).' : 'Comercial con el ciclo del compresor ≈ 250 W las 24 h (≈ 180 kWh al mes cada uno).');
    if (!casa) {
      const congeladores = cuantosDe(t, ['congeladore?s?']);
      if (congeladores) poner('Congeladores', congeladores, 180, 24, 12, 'Congelador horizontal comercial con el calor de Sonora ≈ 180 W promedio las 24 h (≈ 130 kWh al mes cada uno).');
      const camaras = cuantosDe(t, [CAMARA_FRIA]);
      if (camaras) poner('Cámaras frías', camaras, 1200, 24, 12, 'Cámara de refrigeración: unidad condensadora de ~2 HP (≈ 2,400 W) trabajando la mitad del tiempo ≈ 1,200 W las 24 h (≈ 880 kWh al mes cada una).');
      const pcs = cuantos(t, 'computadoras?|pcs?\\b|equipos? de computo');
      if (pcs) poner('Computadoras', pcs, 150, 9, 12, '150 W cada una, 9 h al día.');
    }
  } else if (casa) {
    const r = recamaras ?? 3;
    if (recamaras === null) supuestos.push('No dijo recámaras: se supusieron 3 para las luces.');
    poner('Refrigerador', cuantos(t, 'refrigeradore?s?|refris?') ?? 1, 60, 24, 12, 'Promedio con el ciclo del compresor ≈ 60 W las 24 h (≈ 44 kWh al mes).');
    poner('Luces LED', (r + 3) * 3 * (pisos && pisos > 1 ? 1.3 : 1), 10, 5, 12, `${r} recámaras más sala, cocina y baños: unos 3 focos de 10 W por cuarto, 5 h al día.`);
    poner('TV y varios', 1, 300, 4, 12, 'TV, cocina y cargadores: lo que toda casa tiene enchufado, 300 W unas 4 h al día.');
  } else {
    const m2 = cuantos(t, 'm2|metros? cuadrados?|mts?2?\\b');
    poner('Luces del negocio', m2 ? m2 / 5 : 20, 18, 12, 12, m2 ? `Un tubo LED de 18 W por cada 5 m² (${numero(m2)} m²), 12 h al día.` : 'No dijo metros: 20 tubos LED de 18 W, 12 h al día.');
    // Revisión de ventas (22-oct): refrigeradores, vitrinas, congeladores y cámaras se cuentan cada uno (antes solo el primero
    // que decía la descripción, y el congelador o la cámara se perdían). Consumos típicos con el calor de Sonora, a la vista.
    const refris = cuantosDe(t, ['(?:refrigeradore?s?|refris?)\\b', 'vitrinas?']); // «refri» no es «refrigeración»
    poner('Refrigeradores / vitrinas', refris ?? 1, 250, 24, 12, `Comercial con el ciclo del compresor ≈ 250 W las 24 h (≈ 180 kWh al mes cada uno)${refris === null ? '; no dijo cuántos: se supuso 1' : ''}.`);
    const congeladores = cuantosDe(t, ['congeladore?s?']);
    if (congeladores) poner('Congeladores', congeladores, 180, 24, 12, 'Congelador horizontal comercial con el calor de Sonora ≈ 180 W promedio las 24 h (≈ 130 kWh al mes cada uno).');
    const camaras = cuantosDe(t, [CAMARA_FRIA]);
    if (camaras) poner('Cámaras frías', camaras, 1200, 24, 12, 'Cámara de refrigeración: unidad condensadora de ~2 HP (≈ 2,400 W) trabajando la mitad del tiempo ≈ 1,200 W las 24 h (≈ 880 kWh al mes cada una).');
    const pcs = cuantos(t, 'computadoras?|pcs?\\b|equipos? de computo');
    if (pcs) poner('Computadoras', pcs, 150, 9, 12, '150 W cada una, 9 h al día.');
    poner('Varios del negocio', 1, 500, 10, 12, 'Lo que todo negocio tiene enchufado: 500 W unas 10 h al día.');
  }
  if (casa && menciona(t, 'congeladore?s?')) poner('Congelador', cuantos(t, 'congeladore?s?') ?? 1, 50, 24, 12, 'Congelador de casa ≈ 50 W promedio las 24 h (≈ 36 kWh al mes).');
  // Compresores (de aire, de taller): por su tamaño en HP; sin tamaño, 2 HP. Trabajan a ratos: ~3 h al día a plena carga.
  const porHp = sumaPorEquipo(t, 'compresore?s?', 'hp|caballos?');
  const compresores = porHp?.equipos ?? cuantosDe(t, ['compresore?s?']);
  if (compresores) {
    const hp = porHp ? porHp.total / porHp.equipos : 2;
    poner('Compresores', compresores, hp * 746, 3, 12, `${numero(hp, Number.isInteger(hp) ? 0 : 1)} HP ≈ ${numero(Math.round(hp * 746))} W cada uno${porHp ? '' : ' (no dijo los HP: se supusieron 2)'}; ~3 h al día a plena carga (arranca y para). Si trabaja todo el turno, súbele horas.`);
  }
  if (menciona(t, 'alberca|piscina|bomba')) {
    const hp = cuantos(t, 'hp|caballos?');
    poner('Bomba de alberca', 1, hp ? hp * 746 : 1100, 6, 12, `${hp ? `${numero(hp, Number.isInteger(hp) ? 0 : 1)} HP` : '1.5 HP'} ≈ ${numero(hp ? hp * 746 : 1100)} W, 6 h al día todo el año.`);
  }
  if (menciona(t, 'carros? electric|autos? electric|coches? electric|vehiculos? electric|tesla|\\bev\\b')) {
    poner('Carro eléctrico', cuantos(t, 'carros?|autos?|coches?|vehiculos?') ?? 1, 3600, 2, 12, '≈ 40 km al día × 0.18 kWh/km ≈ 7.2 kWh al día (cargador de 3.6 kW, 2 h).');
  }
  if (menciona(t, 'estufa electrica|parrilla|induccion|horno electrico')) poner('Estufa / horno eléctrico', 1, 2000, 1.5, 12, '2,000 W, hora y media al día.');
  if (menciona(t, 'calentador electrico|boiler electrico|calenton|calentador de agua electrico|boiler')) poner('Calentador eléctrico', 1, 3000, 2, 12, '3,000 W, 2 h al día (regaderas de la familia).');
  if (menciona(t, 'secadora')) poner('Secadora eléctrica', 1, 3000, 0.7, 12, '3,000 W, unos 40 min al día.');
  if (menciona(t, 'lavadora')) poner('Lavadora', 1, 500, 1, 12, '500 W, 1 h al día.');
  if (!casa && ton !== null && ton > 0) supuestos.push('Aires del negocio: 8 h al día de abril a octubre y 3 h más en pleno verano; si abre más horas, súbelas.');
  if (pisos && pisos > 1 && casa && !soloNuevo) supuestos.push(`${numero(pisos)} pisos: más luces (los aires ya van por toneladas).`);
  if (soloNuevo) supuestos.push('Solo lo nuevo: lo que ya tiene (luces, refrigeradores, varios) ya está en su recibo.');
  supuestos.push('Es una estimación (±15 %): corrige cantidades u horas si el cliente te dio otro dato.');
  return { cargas, supuestos };
}

/** De las ediciones que propuso «corregir» (ya comprobadas) al cuadro de cargas que ve el vendedor. */
export function cargasDeEdiciones(ediciones: readonly { op: string; ruta: string; despues?: unknown; motivo?: string | null }[]): CargaEstimada[] {
  const out: CargaEstimada[] = [];
  for (const e of ediciones) {
    if (e.op !== 'agregar' || !/^\/cuestionario\/estimacion\/cargas\/(-|\d+)$/.test(e.ruta)) continue;
    const c = e.despues as Partial<Carga> | null | undefined;
    if (!c || typeof c.equipo !== 'string' || !(Number(c.cantidad) >= 1) || !(Number(c.w) > 0) || !(Number(c.horas_dia) >= 0) || !(Number(c.meses_uso) >= 1)) continue;
    out.push({
      carga: { equipo: c.equipo, cantidad: Number(c.cantidad), w: Number(c.w), horas_dia: Number(c.horas_dia), meses_uso: Math.min(12, Math.round(Number(c.meses_uso))) },
      motivo: (e.motivo ?? '').trim(),
    });
  }
  return out;
}

/** Los supuestos generales de «entendi» (después de «Supuestos:», separados por «|»). */
export function supuestosDeTexto(texto: string): string[] {
  const i = texto.search(/supuestos\s*:/i);
  if (i < 0) return [];
  // Verificación del 28-oct: la explicación de «corregir» sigue con sus frases de siempre («Propongo 2 cambio(s); revísalos y
  // aplícalos si están bien.»); no son supuestos y confundían junto a «Usar este aumento».
  const resto = texto.slice(i);
  const fin = resto.search(/\s(?:Propongo \d|No propongo \d|Pregunta: |Eso no se cambia aquí: |No encontré nada que cambiar)/);
  return (fin >= 0 ? resto.slice(0, fin) : resto)
    .replace(/^supuestos\s*:/i, '')
    .split('|')
    .map((s) => s.trim())
    .filter((s) => s.length > 1)
    .slice(0, 12);
}
